import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Stack,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import Sidebar from './Navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStoreActions, useStoreState } from '../store';
import { redishColor } from '../utils/theme';
import usePlayerInfo from '../hooks/usePlayerInfo';
import Footer from './Footer';
import NotFound from './NotFound';
import PaymentInvoice from './PaymentInvoice';
import {
  verifyHmac,
  getPaymentStatus,
  checkPayment,
  PaymentStatusPayload,
} from '../utils/api';
import useEffectOnce from '../hooks/useEffectOnce';
import { timeout } from '../utils/helper';
import Loader from './Loader';

const verifyArray =
  'amount_cents,created_at,currency,error_occured,has_parent_transaction,id,integration_id,is_3d_secure,is_auth,is_capture,is_refunded,is_standalone_payment,is_voided,order,owner,pending,source_data.pan,source_data.sub_type,source_data.type,success';

type Phase = 'verifying' | 'reconciling' | 'success' | 'failed' | 'pending';

// Poll config: try the DB every 2s; after 10s kick off an explicit Paymob
// reconciliation (checkPayment); stop polling after 40s and show the pending
// message — the webhook or the hourly cron will eventually reconcile and the
// invoice email will land.
const POLL_INTERVAL_MS = 2000;
const RECONCILE_AFTER_MS = 10000;
const MAX_POLL_MS = 40000;

const PaymentState = () => {
  const playerInfo = usePlayerInfo();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState<Phase>('verifying');
  const [paymentRecord, setPaymentRecord] =
    useState<PaymentStatusPayload | null>(null);

  const stoppedRef = useRef(false);
  const reconciledRef = useRef(false);

  const params = useMemo(() => {
    const obj: any = {};
    searchParams.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }, [searchParams]);

  const neededHMACParams = useMemo(() => {
    return verifyArray.split(',').map((key: string) => {
      return params[key];
    });
  }, [params]);

  const { userModel: userState } = useStoreState((state) => state);
  const { isDark } = userState;
  const { userModel: userActions } = useStoreActions((state) => state);

  useEffect(() => {
    if (isDark) {
      userActions.setIsDark(false);
    }
  }, [isDark, userActions]);

  const pollPaymentStatus = useCallback(async () => {
    const orderId = params?.order ? String(params.order) : undefined;
    const merchantOrderId = params?.merchant_order_id
      ? String(params.merchant_order_id)
      : undefined;

    if (!orderId && !merchantOrderId) {
      // No identifier in URL — can't reconcile. Fall back to URL params.
      if (params?.success === 'true') {
        setPhase('success');
      } else if (params?.pending === 'true') {
        setPhase('pending');
      } else {
        setPhase('failed');
      }
      return;
    }

    const tryReconcile = async (p: PaymentStatusPayload) => {
      if (reconciledRef.current) return;
      if (!p.orderId || !p.intentionId) return;
      reconciledRef.current = true;
      setPhase('reconciling');
      try {
        await checkPayment({
          id: p.id,
          orderId: p.orderId,
          intentionId: p.intentionId,
        });
      } catch (err) {
        console.log('checkPayment failed', err);
      }
    };

    const start = Date.now();
    while (!stoppedRef.current) {
      const elapsed = Date.now() - start;

      try {
        const { data } = await getPaymentStatus(
          merchantOrderId ? { id: merchantOrderId } : { orderId }
        );

        if (data.status === 'success' && data.payment) {
          setPaymentRecord(data.payment);

          if (data.payment.isFulfilled) {
            if (data.payment.success) {
              setPhase('success');
              return;
            }
            if (data.payment.pending) {
              // Async payment method (Fawry/wallet) awaiting confirmation —
              // show pending and let email reconcile when it settles.
              setPhase('pending');
              return;
            }
            // isFulfilled && !success && !pending: either genuinely failed,
            // or a failed first attempt in a retry scenario (a later
            // successful webhook upgrades false→true). Force a Paymob
            // reconciliation once to get the authoritative state; if it
            // still says failed after that, it's really failed.
            if (!reconciledRef.current) {
              await tryReconcile(data.payment);
            } else if (elapsed >= MAX_POLL_MS) {
              setPhase('failed');
              return;
            }
          } else if (
            !reconciledRef.current &&
            elapsed >= RECONCILE_AFTER_MS
          ) {
            // Not yet fulfilled after RECONCILE_AFTER_MS — force a Paymob
            // reconciliation.
            await tryReconcile(data.payment);
          }
        }
      } catch (err) {
        console.log('getPaymentStatus failed', err);
      }

      // Time cap runs regardless of response shape — including "payment not
      // found" cases where we'd otherwise loop forever.
      if (Date.now() - start >= MAX_POLL_MS) {
        setPhase('pending');
        return;
      }

      await timeout(POLL_INTERVAL_MS);
    }
  }, [params]);

  const verifyHash = useCallback(async () => {
    if (!params) return;

    if (!params?.hmac || !neededHMACParams.length) {
      setError(true);
      return;
    }

    try {
      await timeout(1000);
      const response = await verifyHmac(
        params.hmac,
        neededHMACParams.join('')
      );

      if (response.status !== 200) {
        setError(true);
        return;
      }

      await pollPaymentStatus();
    } catch (err) {
      console.log(err);
      setError(true);
    }
  }, [neededHMACParams, params, pollPaymentStatus]);

  useEffectOnce(() => {
    verifyHash();
    return () => {
      stoppedRef.current = true;
    };
  });

  const renderContent = () => {
    if (error) {
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Payment verification failed</AlertTitle>
          <AlertDescription>
            We couldn't verify your payment. If you were charged, please contact
            support.
          </AlertDescription>
        </Alert>
      );
    }

    if (phase === 'verifying' || phase === 'reconciling') {
      return (
        <Box>
          <Alert status="info" mb="20px">
            <AlertIcon />
            <AlertTitle>Confirming your payment…</AlertTitle>
            <AlertDescription>
              This usually takes a few seconds. Please don't close this page.
            </AlertDescription>
          </Alert>
          <Loader />
        </Box>
      );
    }

    if (phase === 'failed') {
      return (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Payment failed</AlertTitle>
          <AlertDescription>
            Something happened during your payment. You can try again or
            contact support if you were charged.
          </AlertDescription>
        </Alert>
      );
    }

    if (phase === 'pending') {
      return (
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>Payment pending</AlertTitle>
          <AlertDescription>
            Your payment is still being processed by the bank. We'll send you a
            receipt by email once it's confirmed.
          </AlertDescription>
        </Alert>
      );
    }

    // success
    return (
      <Box>
        <PaymentInvoice
          params={{
            ...params,
            email: playerInfo?.email,
            success: 'true',
            order: paymentRecord?.orderId ?? params?.order,
          }}
        />
        <Box textAlign="center" mt="30px">
          <Button variant={'link'} color={redishColor} as="a" href="/">
            Go to Home
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Sidebar>
      <Stack
        bg="white"
        minH="100vh"
        pt={{ base: '125px', md: '200px' }}
        display="flex"
        justifyContent={'space-between'}
      >
        {!playerInfo?.id ? (
          <NotFound />
        ) : (
          <Box maxW="600px" margin="0 auto 0 auto" p="15px">
            {renderContent()}
          </Box>
        )}
        <Footer />
      </Stack>
    </Sidebar>
  );
};

export default PaymentState;
